import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSvgTooltip } from './useSvgTooltip';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type UseLayerTooltipOptions<TItem> = {
  /** Items to track. Accepts `undefined` for optional/async data. */
  items: TItem[] | undefined;
  /**
   * Extracts a unique string ID from each item.
   * The returned value must match the `data-item-id` attribute set on the corresponding SVG element.
   */
  getId: (item: TItem) => string;
};

type UseLayerTooltipResult<TItem> = {
  /** Attach to the parent `<g>` element that wraps all feature elements. */
  layerRef: React.RefObject<SVGGElement | null>;
  /** Pass directly to `<SvgTooltip ref={tooltipRef}>`. */
  tooltipRef: React.RefObject<HTMLDivElement | null>;
  /** The currently hovered item, or `null` when nothing is hovered. */
  hoveredItem: TItem | null;
};

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

/**
 * Provides event-delegation–based tooltip tracking for SVG canvas layers.
 *
 * Attaches a single `pointermove`/`pointerleave` listener to the parent `<g>` element
 * instead of individual listeners on each feature. Hover state updates only re-render
 * when the hovered item changes; tooltip position is updated imperatively.
 *
 * Feature elements must set `data-item-id={getId(item)}` so the hook can identify
 * which item is under the pointer via `closest('[data-item-id]')`.
 *
 * @example
 * ```tsx
 * const { layerRef, tooltipRef, hoveredItem } = useLayerTooltip({
 *   items: features,
 *   getId: (f) => f.id,
 * });
 *
 * return (
 *   <>
 *     <g ref={layerRef}>
 *       {features.map((f) => (
 *         <g key={f.id} data-item-id={f.id}>
 *           ...render feature geometry here
 *         </g>
 *       ))}
 *     </g>
 *     <SvgTooltip ref={tooltipRef} visible={!!hoveredItem}>
 *       {hoveredItem?.name}
 *     </SvgTooltip>
 *   </>
 * );
 * ```
 */
const useLayerTooltip = <TItem>({ items, getId }: UseLayerTooltipOptions<TItem>): UseLayerTooltipResult<TItem> => {
  const layerRef = useRef<SVGGElement>(null);
  const { tooltipRef, updatePosition } = useSvgTooltip();
  const [hoveredItem, setHoveredItem] = useState<TItem | null>(null);

  const itemById = useMemo(() => new Map(items?.map((item) => [getId(item), item])), [items, getId]);

  // Clear hoveredItem when it's no longer in itemById.
  // If items refreshes and the currently hovered item is no longer present in the new dataset, hoveredItem retains a reference to the removed item,
  // keeping the tooltip populated with stale data until the next pointer event.
  useEffect(() => {
    setHoveredItem((prev) => (prev !== null && !itemById.has(getId(prev)) ? null : prev));
  }, [itemById, getId]);

  const getIdFromEvent = useCallback((event: PointerEvent): string | null => {
    const target = (event.target as Element | null)?.closest<SVGGElement>('[data-item-id]');
    return target?.getAttribute('data-item-id') ?? null;
  }, []);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const id = getIdFromEvent(event);

      if (!id) {
        setHoveredItem(null);
        return;
      }

      // Only trigger a React re-render when the hovered item changes.
      setHoveredItem((prev) => {
        if (prev !== null && getId(prev) === id) return prev;
        return itemById.get(id) ?? null;
      });

      // Position update is imperative — no re-render needed.
      updatePosition(event.clientX, event.clientY);
    },
    [getIdFromEvent, itemById, updatePosition, getId],
  );

  const handlePointerLeave = useCallback(() => {
    setHoveredItem(null);
  }, []);

  useEffect(() => {
    const node = layerRef.current;
    if (!node) return;

    node.addEventListener('pointermove', handlePointerMove);
    node.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      node.removeEventListener('pointermove', handlePointerMove);
      node.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [handlePointerMove, handlePointerLeave]);

  return { layerRef, tooltipRef, hoveredItem };
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { UseLayerTooltipOptions, UseLayerTooltipResult };
export { useLayerTooltip };
